// App-wide single-step-at-a-time undo stack. The dataProvider records an inverse
// operation for every create/update/delete; ⌘Z / Ctrl+Z (see UndoManager) pops
// and runs the most recent one. Undo ops call the Directus SDK directly so they
// are NOT themselves recorded (no infinite loop / redo).
import { createItem, updateItem, deleteItem } from "../providers/api.ts";
import { directus } from "@/providers/directus";

// `resource` is the collection the inverse operation writes. UndoManager uses
// it to invalidate that collection's queries and nothing else -- see lib/cache.
export type UndoEntry = { label: string; resource: string; run: () => Promise<void> };
type Ev = { type: "push" | "undo" | "empty" | "error"; label?: string; hint?: boolean };
type Listener = (e: Ev) => void;

const stack: UndoEntry[] = [];
const MAX = 40;
const listeners = new Set<Listener>();
const emit = (e: Ev) => listeners.forEach((l) => l(e));

export function subscribeUndo(l: Listener): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}
export function canUndo(): boolean { return stack.length > 0; }

// hint=true surfaces a "⌘Z to undo" toast (used for the meaningful create/delete
// actions); updates are still undoable but don't nag on every field edit.
export function pushUndo(entry: UndoEntry, hint = true): void {
  stack.push(entry);
  if (stack.length > MAX) stack.shift();
  emit({ type: "push", label: entry.label, hint });
}

// Returns what was undone (label for the toast, resource for the cache) so the
// caller does not have to guess which collection changed.
export async function runLastUndo(): Promise<{ label: string; resource: string } | null> {
  const e = stack.pop();
  if (!e) { emit({ type: "empty" }); return null; }
  try {
    await e.run();
    emit({ type: "undo", label: e.label });
    return { label: e.label, resource: e.resource };
  } catch (err) {
    emit({ type: "error", label: e.label });
    throw err;
  }
}

// Fields Directus manages itself — never send them back on a re-create.
const SYSTEM_FIELDS = new Set(["date_created", "date_updated", "user_created", "user_updated"]);
const idOf = (v: any) => (v && typeof v === "object" && !Array.isArray(v) && "id" in v ? v.id : v);

// Strip system fields + flatten any expanded relations to their id, so a deleted
// record can be re-created. Keeps the original id where the collection allows it,
// so references (comments, sub-tasks…) reattach.
function reCreatable(rec: any): any {
  const o: any = {};
  for (const k of Object.keys(rec || {})) {
    if (SYSTEM_FIELDS.has(k)) continue;
    o[k] = idOf(rec[k]);
  }
  return o;
}

const nice = (resource: string) => resource.replace(/_/g, " ").replace(/s$/, "");

export const inverseOfCreate = (resource: string, created: any): UndoEntry => ({
  label: `Create ${nice(resource)}`,
  resource,
  run: async () => { await directus.request(deleteItem(resource as any, created.id as any)); },
});

export const inverseOfUpdate = (resource: string, id: any, oldValues: any): UndoEntry => ({
  label: `Edit ${nice(resource)}`,
  resource,
  run: async () => { await directus.request(updateItem(resource as any, id as string, oldValues as any)); },
});

export const inverseOfDelete = (resource: string, record: any): UndoEntry => {
  const payload = reCreatable(record);
  return {
    label: `Delete ${nice(resource)}`,
    resource,
    run: async () => { await directus.request(createItem(resource as any, payload as any)); },
  };
};

// Noisy / system collections we don't record undo for.
const SKIP = new Set(["directus_files", "directus_users"]);
export const isUndoable = (resource: string) => !SKIP.has(resource);
export const normalizeOld = (v: any) => idOf(v);
